import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, database } from '../firebase';
import { EmailAuthProvider, reauthenticateWithCredential, deleteUser, signInWithEmailAndPassword } from 'firebase/auth';
import { ref, remove } from 'firebase/database';
import './DeleteAccountPage.css';

const DeleteAccountPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('login'); // 'login', 'confirm', or 'authenticate'
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      setLoading(false);
      
      if (currentUser) {
        setStep('confirm');
        setLoginEmail(currentUser.email);
      } else {
        setStep('login');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginEmail.trim() || !loginPassword.trim()) {
      setError('Email and password are required');
      return;
    }

    setIsLoggingIn(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      // User state will be updated by the auth state listener
      setLoginPassword(''); // Clear login password
    } catch (error) {
      setIsLoggingIn(false);
      console.error('Login error:', error);
      
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Login failed: ' + error.message);
      }
    }
  };

  const handleInitialConfirm = () => {
    setStep('authenticate');
    setError('');
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      // Delete user data from database first
      const userRef = ref(db, `users/${user.uid}`);
      await remove(userRef);

      // Delete the user account
      await deleteUser(user);

      // Redirect to home page
      navigate('/');
      
    } catch (error) {
      setIsDeleting(false);
      console.error('Error deleting account:', error);
      
      if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Failed to delete account: ' + error.message);
      }
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleBackToLogin = () => {
    setStep('login');
    setPassword('');
    setError('');
  };

  if (loading) {
    return (
      <div className="delete-account-container">
        <div className="delete-account-card">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="delete-account-container">
      <div className="delete-account-card">
        {step === 'login' ? (
          <>
            <h2>Delete Account</h2>
            <p className="login-instruction">
              Please log in to your account to proceed with deletion:
            </p>
            
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="loginEmail">Email:</label>
                <input
                  type="email"
                  id="loginEmail"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={isLoggingIn}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="loginPassword">Password:</label>
                <input
                  type="password"
                  id="loginPassword"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoggingIn}
                  required
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="button-group">
                <button 
                  type="button" 
                  onClick={handleCancel} 
                  className="cancel-button"
                  disabled={isLoggingIn}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="login-button" 
                  disabled={isLoggingIn}
                >
                  {isLoggingIn ? 'Logging in...' : 'Login'}
                </button>
              </div>
            </form>
          </>
        ) : step === 'confirm' ? (
          <>
            <h2>Delete Account</h2>
            <div className="warning-section">
              <div className="warning-icon">⚠️</div>
              <p>
                <strong>This action cannot be undone.</strong> All your data will be permanently deleted, including:
              </p>
              <ul>
                <li>Your account information</li>
                <li>All financial data and transactions</li>
                <li>Subscription information</li>
                <li>All saved preferences and settings</li>
              </ul>
            </div>
            
            <div className="user-info">
              <p><strong>Account:</strong> {user.email}</p>
            </div>

            <div className="button-group">
              <button onClick={handleBackToLogin} className="cancel-button">
                Log Out & Cancel
              </button>
              <button onClick={handleInitialConfirm} className="delete-button">
                Continue with Deletion
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Confirm Account Deletion</h2>
            <p>Please re-enter your password to confirm account deletion:</p>
            
            <form onSubmit={handleDeleteAccount}>
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  disabled={isDeleting}
                  required
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="button-group">
                <button 
                  type="button" 
                  onClick={() => setStep('confirm')} 
                  className="cancel-button"
                  disabled={isDeleting}
                >
                  Back
                </button>
                <button 
                  type="submit" 
                  className="delete-button" 
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting Account...' : 'Delete Account'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountPage;